import React, { ReactNode, useRef, useState, useEffect } from 'react';
import { DecisionVoucherProvider } from '../../context';
import { InfoDisplayProvider } from '../../context/InfoDisplayContext';
import MainColumn from './MainColumn';
import RightColumn from './RightColumn';
import LeftColumn from './LeftColumn';

interface DecisionVoucherLayoutProps {
  savId: number;
  children?: ReactNode;
}

const MIN_LEFT_COLUMN_WIDTH = 140; // Minimum en pixels pour le panneau gauche
const MIN_RIGHT_COLUMN_WIDTH = 180; // Minimum en pixels pour le panneau droit
const DEFAULT_LEFT_WIDTH = 20; // Pourcentage
const DEFAULT_RIGHT_WIDTH = 30; // Pourcentage
const MAX_LEFT_WIDTH = 30; // Pourcentage maximum
const MAX_RIGHT_WIDTH = 45; // Pourcentage maximum pour le panneau droit

const DecisionVoucherLayout: React.FC<DecisionVoucherLayoutProps> = ({
  savId,
  children
}) => {
  // Largeurs par défaut et refs
  const [leftWidth, setLeftWidth] = useState<number>(DEFAULT_LEFT_WIDTH);
  const [rightWidth, setRightWidth] = useState<number>(DEFAULT_RIGHT_WIDTH);
  const containerRef = useRef<HTMLDivElement>(null);

  // État pour le redimensionnement en cours
  const [resizing, setResizing] = useState<'left' | 'right' | null>(null);
  const [startX, setStartX] = useState<number>(0);
  const [startLeftWidth, setStartLeftWidth] = useState<number>(0);
  const [startRightWidth, setStartRightWidth] = useState<number>(0);

  // Gestionnaire de début de redimensionnement
  const startResize = (event: React.MouseEvent, type: 'left' | 'right') => {
    event.preventDefault();
    setResizing(type);
    setStartX(event.clientX);
    setStartLeftWidth(leftWidth);
    setStartRightWidth(rightWidth);
  };

  // Gestionnaire de redimensionnement
  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!resizing || !containerRef.current || !startX) return;

      // Obtenir la largeur totale du conteneur
      const containerWidth = containerRef.current.offsetWidth;

      // Calculer le déplacement en pourcentage
      const movementX = event.clientX - startX;
      const movementPercent = (movementX / containerWidth) * 100;

      // Obtenir les largeurs minimales en pourcentage pour chaque panneau
      const minLeftWidthPercent = (MIN_LEFT_COLUMN_WIDTH / containerWidth) * 100;
      const minRightWidthPercent = (MIN_RIGHT_COLUMN_WIDTH / containerWidth) * 100;

      // Appliquer les nouvelles largeurs selon le resizer actif
      if (resizing === 'left') {
        const newLeftWidth = Math.max(minLeftWidthPercent, Math.min(MAX_LEFT_WIDTH, startLeftWidth + movementPercent));
        setLeftWidth(newLeftWidth);
      } else if (resizing === 'right') {
        const newRightWidth = Math.max(minRightWidthPercent, Math.min(MAX_RIGHT_WIDTH, startRightWidth - movementPercent));
        setRightWidth(newRightWidth);
      }
    };

    const stopResize = () => {
      setResizing(null);
    };

    if (resizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopResize);
      // Ajouter la classe "resizing" au body pour modifier le curseur partout
      document.body.classList.add('resizing');
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResize);
      // Retirer la classe "resizing" lorsque terminé
      document.body.classList.remove('resizing');
    };
  }, [resizing, startX, startLeftWidth, startRightWidth]);

  // Calculer la largeur de la colonne centrale
  const centerWidth = 100 - leftWidth - rightWidth;

  // Double-clic sur un redimensionneur pour réinitialiser à la taille par défaut
  const handleDoubleClick = (type: 'left' | 'right') => {
    if (type === 'left') {
      setLeftWidth(DEFAULT_LEFT_WIDTH);
    } else {
      setRightWidth(DEFAULT_RIGHT_WIDTH);
    }
  };

  return (
    <DecisionVoucherProvider savId={savId}>
      <InfoDisplayProvider>
        <style dangerouslySetInnerHTML={{
          __html: `
            body.resizing {
              cursor: col-resize !important;
              user-select: none;
            }
            body.resizing * {
              cursor: col-resize !important;
            }
          `
        }} />
        <div className="tw-w-full tw-h-full tw-flex tw-flex-col">
          {/* Layout à trois colonnes redimensionnables */}
          <div
            ref={containerRef}
            className="tw-flex tw-flex-col xl:tw-flex-row tw-flex-grow tw-overflow-hidden tw-relative"
          >
            {/* Colonne gauche - Navigation et contexte */}
            <div
              className="xl:tw-bg-white tw-border-r tw-border-gray-200 tw-overflow-y-auto tw-relative tw-flex-shrink-0 tw-min-w-[100px]"
              style={{ width: `${leftWidth}%` }}
            >
              <LeftColumn />

              {/* Poignée de redimensionnement pour la colonne gauche */}
              <div
                className={`tw-hidden xl:tw-block tw-absolute tw-top-0 tw-right-0 tw-bottom-0 tw-w-1 tw-cursor-col-resize tw-z-10 hover:tw-bg-blue-400 active:tw-bg-blue-500 ${resizing === 'left' ? 'tw-bg-blue-500' : 'tw-bg-gray-200'}`}
                onMouseDown={(e) => startResize(e, 'left')}
                onDoubleClick={() => handleDoubleClick('left')}
              >
                <div className="tw-absolute tw-top-0 tw-right-0 tw-bottom-0 tw-w-5 tw-transform tw-translate-x-[-50%] tw-cursor-col-resize"></div>
                {/* Indicateur de redimensionnement */}
                <div className="tw-absolute tw-top-1/2 tw-right-0 tw-transform tw-translate-y-[-50%] tw-translate-x-[0.5px] tw-w-[3px] tw-h-6 tw-rounded-full tw-opacity-0 hover:tw-opacity-100 tw-bg-blue-500 tw-shadow-sm"></div>
              </div>
            </div>

            {/* Colonne centrale - Formulaire principal */}
            <div
              className="xl:tw-bg-white tw-flex tw-flex-col tw-overflow-y-auto tw-flex-grow-0 tw-flex-shrink-0 tw-min-w-[300px]"
              style={{ width: `${centerWidth}%` }}
            >
              <MainColumn>
                {children}
              </MainColumn>
            </div>

            {/* Colonne droite - Prévisualisation et infos supplémentaires */}
            <div
              className="xl:tw-bg-white tw-border-l tw-border-gray-200 tw-overflow-y-auto tw-relative tw-flex-shrink-0 tw-min-w-[100px]"
              style={{ width: `${rightWidth}%` }}
            >
              {/* Poignée de redimensionnement pour la colonne droite */}
              <div
                className={`tw-hidden xl:tw-block tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-w-1 tw-cursor-col-resize tw-z-10 hover:tw-bg-blue-400 active:tw-bg-blue-500 ${resizing === 'right' ? 'tw-bg-blue-500' : 'tw-bg-gray-200'}`}
                onMouseDown={(e) => startResize(e, 'right')}
                onDoubleClick={() => handleDoubleClick('right')}
              >
                <div className="tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-w-5 tw-transform tw-translate-x-[-50%] tw-cursor-col-resize"></div>
                {/* Indicateur de redimensionnement */}
                <div className="tw-absolute tw-top-1/2 tw-left-0 tw-transform tw-translate-y-[-50%] tw-translate-x-[-0.5px] tw-w-[3px] tw-h-6 tw-rounded-full tw-opacity-0 hover:tw-opacity-100 tw-bg-blue-500 tw-shadow-sm"></div>
              </div>

              <RightColumn />
            </div>
          </div>
        </div>
      </InfoDisplayProvider>
    </DecisionVoucherProvider>
  );
};

export default DecisionVoucherLayout;